import React, { FC, useCallback, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
  DatePickers,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Divider,
  Button,
  DriftIcons,
  IconButton,
} from 'drift-ui';
import { RootState } from 'app/configureStore';
import { useDispatch, useSelector } from 'react-redux';
import { sendDOFirebaseEvent } from 'utils/utils';
import {
  Applicant,
  phoneFormatter,
  useSectionFormHelpers,
  maskSSN,
  ssnFormatter,
  generalTextRegex,
  phoneRegex,
  ssnOrItinRegex,
  zipRegex,
  DO_APPLICATION_TYPE,
  emailRegex,
  SUFFIX_OPTIONS,
  STATE_OPTIONS_WITH_ARMED_FORCES,
  OptionalString,
  findDuplicateIndices,
  getPrefillBannerSectionStatus,
  BankingApplicationDocument,
  isPrefillFieldLocked,
} from 'client-experience-toolbox';
import { useFormNetworkCalls } from 'utils/useFormNetworkCalls';
import styles from './OwnershipInfo.scss';
import { useTranslation } from 'react-i18next';
import { useUpdateApplication } from '../../utils/useUpdateApplication';
import { ContinueButton } from '../../components/ContinueButton';
import { v4 as uuidv4 } from 'uuid';
import baseStyles from './BaseFormStyles.scss';
import cx from 'classnames';
import { RemoveOwnerConfirmationModal } from '../../components/RemoveOwnerConfirmationModal';
import { PrefillBanner } from 'components/PrefillBanner';

const { AddCircleIcon } = DriftIcons;

export const OwnershipInformation: FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const application = useSelector((state: RootState) => state?.applicationReducer?.bankingApplication);
  const applicationData = application?.applicationData;
  const hasMajorOwner = applicationData?.company?.hasMajorOwner;
  const isSoleProp = applicationData?.company?.ownershipType === 'soleProprietor';
  const allowAdditionalOwners = !isSoleProp && hasMajorOwner;
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [isThrottled, setIsThrottled] = useState(false);
  const updateApplication = useUpdateApplication(DO_APPLICATION_TYPE.BANKING);

  const formRef = useRef<HTMLDivElement>(null);
  const [isPatching, setIsPatching] = useState(false);
  const [editingSSNIndex, setEditingSSNIndex] = useState<boolean[]>([]);
  const [showRemoveOwnerModal, setShowRemoveOwnerModal] = useState(false);
  const [ownerToRemove, setOwnerToRemove] = useState<Partial<Applicant> | null>(null);
  const { patchApplication, patchFullApplication, patchApplicationPullData } = useFormNetworkCalls(
    DO_APPLICATION_TYPE.BANKING,
  );
  const prefillBannerStatus = getPrefillBannerSectionStatus(
    (application as BankingApplicationDocument)?.prefilledFields,
    'ownershipInformation',
  );
  const isFieldDisabled = (field: string) => isPrefillFieldLocked(application?.prefilledFields || [], field);

  const getValueSSN = (applicant: Partial<Applicant>, index: number, error?: string) => {
    const currentApplicantSSN = applicant?.ssnOrItin ?? '';
    if (editingSSNIndex[index] || error) {
      return ssnFormatter(currentApplicantSSN);
    } else if (applicant.ssnOrItinDisplay?.length) {
      return maskSSN(applicant.ssnOrItinDisplay, true);
    } else {
      return maskSSN(currentApplicantSSN);
    }
  };

  const {
    textHandlerGenerator,
    numberHandlerGenerator,
    selectDropdownHandlerGenerator,
    dateHandlerGenerator,
    regexValidatorGenerator,
    dateRangeValidatorGenerator,
    presenceValidatorGenerator,
    numberValidatorGenerator,
    clearValidation,
    handleInputFocus,
  } = useSectionFormHelpers({
    errorSetter: setErrors,
    patchApplication,
    updateApplication,
    application,
    fireBaseLogEvent: sendDOFirebaseEvent,
  });

  const applicantFieldValidations = (index: number): { [key: string]: () => boolean } => {
    const validations = {
      ownershipPercentage: numberValidatorGenerator({
        key: `applicationData.applicants[${index}].ownershipPercentage`,
        range: [25, 100],
        errorText: !applicationData?.applicants?.[index].ownershipPercentage
          ? t('digital_onboarding.banking.business_type.required')
          : t('digital_onboarding.banking.owner_info.invalid_ownership_percentage'),
        optional: !allowAdditionalOwners,
      }),
      firstName: regexValidatorGenerator({
        key: `applicationData.applicants[${index}].firstName`,
        regex: generalTextRegex,
        errorText: t('digital_onboarding.banking.business_type.required'),
      }),
      middleName: regexValidatorGenerator({
        key: `applicationData.applicants[${index}].middleName`,
        regex: generalTextRegex,
        errorText: '',
        optional: true,
      }),
      lastName: regexValidatorGenerator({
        key: `applicationData.applicants[${index}].lastName`,
        regex: generalTextRegex,
        errorText: t('digital_onboarding.banking.business_type.required'),
      }),
      dateOfBirth: dateRangeValidatorGenerator({
        key: `applicationData.applicants[${index}].dateOfBirth`,
        range: [undefined, dayjs()],
        errorText: t('digital_onboarding.banking.business_type.required'),
      }),
      ssnOrItin: regexValidatorGenerator({
        key: `applicationData.applicants[${index}].ssnOrItin`,
        regex: ssnOrItinRegex,
        errorText: t('digital_onboarding.banking.business_type.required'),
        skip: Boolean(applicationData?.applicants?.[index]?.ssnOrItinDisplay),
        skipPatch: true,
      }),
      homeStreetAddress: regexValidatorGenerator({
        key: `applicationData.applicants[${index}].homeAddress.street`,
        regex: generalTextRegex,
        errorText: t('digital_onboarding.banking.business_type.required'),
      }),
      secondaryStreetAddress: regexValidatorGenerator({
        key: `applicationData.applicants[${index}].homeAddress.secondaryStreet`,
        regex: generalTextRegex,
        optional: true,
        errorText: '',
      }),
      tertiaryStreetAddress: regexValidatorGenerator({
        key: `applicationData.applicants[${index}].homeAddress.tertiaryStreet`,
        regex: generalTextRegex,
        optional: true,
        errorText: '',
      }),
      state: presenceValidatorGenerator({
        key: `applicationData.applicants[${index}].homeAddress.state`,
        errorText: t('digital_onboarding.banking.business_type.required'),
      }),
      city: regexValidatorGenerator({
        key: `applicationData.applicants[${index}].homeAddress.city`,
        regex: generalTextRegex,
        errorText: t('digital_onboarding.banking.business_type.required'),
      }),
      zipCode: regexValidatorGenerator({
        key: `applicationData.applicants[${index}].homeAddress.zip`,
        regex: zipRegex,
        errorText: t('digital_onboarding.banking.owner_info.invalid_zip'),
      }),
      mobilePhoneNumber: regexValidatorGenerator({
        key: `applicationData.applicants[${index}].mobilePhoneNumber`,
        regex: phoneRegex,
        errorText: !applicationData?.applicants?.[index].mobilePhoneNumber
          ? t('digital_onboarding.banking.business_type.required')
          : t('digital_onboarding.banking.owner_info.invalid_phone'),
      }),
      email: regexValidatorGenerator({
        key: `applicationData.applicants[${index}].email`,
        regex: emailRegex,
        makeLowerCase: true,
        errorText: !applicationData?.applicants?.[index].email
          ? t('digital_onboarding.banking.business_type.required')
          : t('digital_onboarding.common_section.invalid_email'),
        optional: index === 0,
        additionalErrorCheck: (value: OptionalString) => {
          if (!value) return undefined;
          const duplicateIndices = findDuplicateIndices('email', applicationData?.applicants, true);
          return duplicateIndices.includes(index) ||
            applicationData?.controlProng?.email?.toLowerCase() === value?.toLowerCase()
            ? t('digital_onboarding.owner_officer_section.email_duplicate')
            : undefined;
        },
      }),
    };

    return validations;
  };

  const onChangeSSN = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const currentDisplay = getValueSSN(applicationData?.applicants?.[index] || {}, index);
    const hasError = Boolean(errors[`applicationData.applicants[${index}].ssnOrItin`]);
    let value = event.target.value;
    if (!editingSSNIndex[index] && !hasError) {
      const isAppend = value.startsWith(currentDisplay);
      const isBackspace = !isAppend && value.startsWith(currentDisplay.slice(0, -1));

      if (isAppend) value = value.slice(currentDisplay.length);
      else if (isBackspace) value = '';
    }

    // Convert to only digits and then format to remove extra dashes
    value = value.replace(/[^0-9]/g, '');
    value = ssnFormatter(value);

    if (value.length > 11) value = value.slice(0, 11);

    updateApplication(`applicationData.applicants[${index}].ssnOrItin`, value);
    setEditingSSNIndex((prev) => {
      const updated = [...prev];
      updated[index] = true;
      return updated;
    });
    clearValidation(`applicationData.applicants[${index}].ssnOrItin`);
    updateApplication(`applicationData.applicants[${index}].ssnOrItinDisplay`, '');
  };

  const onBlurSSN = async (index: number) => {
    const isValid = applicantFieldValidations(index)?.ssnOrItin();
    const currentApplicantSSN = applicationData?.applicants?.[index]?.ssnOrItin ?? '';
    setEditingSSNIndex((prev) => {
      const updated = [...prev];
      updated[index] = false;
      return updated;
    });

    updateApplication(`applicationData.applicants[${index}].ssnOrItin`, currentApplicantSSN);

    if (isValid && patchApplication) {
      const applicantKey =
        applicationData?.applicants?.[index]?.applicantId || applicationData?.applicants?.[index]?.key;

      updateApplication(`applicationData.applicants[${index}].ssnOrItinDisplay`, currentApplicantSSN.slice(-4));
      await patchApplication(`applicationData.applicants[${index}].ssnOrItin`, currentApplicantSSN, applicantKey);
    }
  };

  const scrollToFirstError = () => {
    const firstErrorInput = formRef.current?.querySelector('.Mui-error') as HTMLElement;
    if (firstErrorInput) {
      firstErrorInput.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      firstErrorInput.focus({ preventScroll: true });
    }
  };

  const handleContinueClick = async () => {
    const isValid = await checkFormValidation();

    if (isValid) {
      return;
    } else {
      scrollToFirstError();
    }
  };

  const checkFormValidation = async () => {
    let firstInvalidApplicant = -1;
    application.applicationData?.applicants?.forEach((_applicant, index) => {
      if (!allowAdditionalOwners && index > 0) {
        return true;
      }

      const results = Object.values(applicantFieldValidations(index)).map((validator) => validator());

      if (firstInvalidApplicant === -1 && results.some((isValid) => !isValid)) {
        firstInvalidApplicant = index;
      }
    });

    if (firstInvalidApplicant !== -1) {
      scrollToFirstError();
      return false;
    } else {
      await patchFullApplication(setIsPatching);
      return true;
    }
  };

  const throttledAddOwner = useCallback(() => {
    if (isThrottled) return;
    setIsThrottled(true);

    const newApplicantKey = uuidv4();
    dispatch({ type: 'ADD_ADDITIONAL_OWNER', ownerKey: newApplicantKey, applicationType: DO_APPLICATION_TYPE.BANKING });
    patchApplication(`applicationData.applicants[${applicationData?.applicants?.length}].key`, newApplicantKey);
    setTimeout(() => {
      setIsThrottled(false);
    }, 300);
  }, [isThrottled, applicationData?.applicants, dispatch, patchApplication]);

  const handleRemoveOwner = (applicant: Partial<Applicant>) => {
    setOwnerToRemove(applicant);
    setShowRemoveOwnerModal(true);
  };

  const handleCancelRemove = () => {
    setShowRemoveOwnerModal(false);
    setOwnerToRemove(null);
  };

  const handleConfirmRemove = async () => {
    if (!ownerToRemove) return;

    const updatedErrors = { ...errors };
    const filteredErrorKeys = Object.keys(updatedErrors).filter(
      (key) =>
        key.includes('applicationData.applicants[1]') ||
        key.includes('applicationData.applicants[2]') ||
        key.includes('applicationData.applicants[3]'),
    );

    for (const key in updatedErrors) {
      if (filteredErrorKeys.includes(key)) {
        delete updatedErrors[key];
      }
    }
    setErrors(updatedErrors);
    if (application && application._id && ownerToRemove.key) {
      dispatch({
        type: 'REMOVE_ADDITIONAL_OWNER',
        owner: ownerToRemove,
        applicationType: DO_APPLICATION_TYPE.BANKING,
      });
      setShowRemoveOwnerModal(false);
      setOwnerToRemove(null);

      await patchApplicationPullData(application._id, {
        'applicationData.applicants': { key: ownerToRemove.key },
      });
    }
  };

  const suffixOptions = (index: number) => {
    return applicationData?.applicants?.[index].suffix ? SUFFIX_OPTIONS : SUFFIX_OPTIONS.slice(0, -1);
  };

  return (
    <div ref={formRef} className={cx([baseStyles.formContainer, styles.ownershipContainer])}>
      <h2 className={styles.header}>
        {allowAdditionalOwners
          ? t('digital_onboarding.banking.owner_info.enter_details_extended')
          : t('digital_onboarding.banking.owner_info.enter_details')}
      </h2>
      <p className={styles.subHeader}>{t('digital_onboarding.banking.owner_info.confirm_names')}</p>
      {prefillBannerStatus !== 'none' && (
        <PrefillBanner hasLockedFields={prefillBannerStatus === 'locked'} isOwnershipSection />
      )}
      {applicationData?.applicants &&
        applicationData?.applicants?.map((applicant, index) => {
          return !allowAdditionalOwners && index > 0 ? null : (
            <div key={index} className={styles.ownerInputView}>
              {index > 0 && (
                <>
                  <Divider className={styles.divider} />
                  <div className={styles.additionalOwnerHeaderView}>
                    <h3 className={styles.additionalOwnerHeader}>
                      {t('digital_onboarding.banking.owner_info.additional_owner', { index })}
                    </h3>
                    <Button onClick={() => handleRemoveOwner(applicant)} variant="text" color="error">
                      {t('digital_onboarding.owner_officer_section.remove_additional_owner_label')}
                    </Button>
                  </div>
                </>
              )}

              <div className={styles.twoColumnRow}>
                <TextField
                  label={t('digital_onboarding.owner_officer_section.label_first_name')}
                  name="firstName"
                  value={applicant?.firstName ?? ''}
                  onChange={textHandlerGenerator(`applicationData.applicants[${index}].firstName`, clearValidation)}
                  onBlur={applicantFieldValidations(index).firstName}
                  error={!!errors[`applicationData.applicants[${index}].firstName`]}
                  onFocus={handleInputFocus}
                  helperText={errors[`applicationData.applicants[${index}].firstName`] ?? ' '}
                  slotProps={{
                    htmlInput: {
                      maxLength: 500,
                      autoCapitalize: 'sentences',
                      'data-nid-target': `owner-info-first-name-${index}`,
                    } as React.InputHTMLAttributes<HTMLInputElement>,
                  }}
                  disabled={isFieldDisabled(`applicationData.applicants.${index}.firstName`)}
                />

                <TextField
                  label={t('digital_onboarding.banking.owner_info.label_middle_name')}
                  name="middleName"
                  value={applicant?.middleName ?? ''}
                  onChange={textHandlerGenerator(`applicationData.applicants[${index}].middleName`, clearValidation)}
                  onBlur={applicantFieldValidations(index).middleName}
                  error={!!errors[`applicationData.applicants[${index}].middleName`]}
                  onFocus={handleInputFocus}
                  helperText={errors[`applicationData.applicants[${index}].middleName`] ?? ' '}
                  slotProps={{
                    htmlInput: {
                      maxLength: 500,
                      autoCapitalize: 'sentences',
                      'data-nid-target': `owner-info-middle-name-${index}`,
                    } as React.InputHTMLAttributes<HTMLInputElement>,
                  }}
                />
              </div>

              <div className={styles.twoColumnRowWithSplit}>
                <TextField
                  label={t('digital_onboarding.owner_officer_section.label_last_name')}
                  name="lastName"
                  value={applicant?.lastName ?? ''}
                  onChange={textHandlerGenerator(`applicationData.applicants[${index}].lastName`, clearValidation)}
                  onBlur={applicantFieldValidations(index).lastName}
                  error={!!errors[`applicationData.applicants[${index}].lastName`]}
                  onFocus={handleInputFocus}
                  helperText={errors[`applicationData.applicants[${index}].lastName`] ?? ' '}
                  slotProps={{
                    htmlInput: {
                      maxLength: 500,
                      autoCapitalize: 'sentences',
                      'data-nid-target': `owner-info-last-name-${index}`,
                    } as React.InputHTMLAttributes<HTMLInputElement>,
                  }}
                  disabled={isFieldDisabled(`applicationData.applicants.${index}.lastName`)}
                />

                <div className={styles.splitColumn}>
                  <FormControl>
                    <InputLabel id={`owner-suffix-label-${index}`}>
                      {t('digital_onboarding.banking.owner_info.label_suffix')}
                    </InputLabel>
                    <Select
                      labelId={`owner-suffix-label-${index}`}
                      label={t('digital_onboarding.banking.owner_info.label_suffix')}
                      onFocus={handleInputFocus}
                      name="ownerSuffix"
                      value={applicant?.suffix ?? ''}
                      onChange={selectDropdownHandlerGenerator(
                        `applicationData.applicants[${index}].suffix`,
                        clearValidation,
                      )}
                      fullWidth
                      slotProps={{
                        input: {
                          'data-nid-target': `owner-info-suffix-${index}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>,
                      }}
                      disabled={isFieldDisabled(`applicationData.applicants.${index}.suffix`)}
                    >
                      {suffixOptions(index).map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  <DatePickers.DatePicker
                    label={t('digital_onboarding.owner_officer_section.label_date_of_birth')}
                    desktopModeMediaQuery="@media (min-width: 768px)"
                    value={applicant?.dateOfBirth ? dayjs(applicant?.dateOfBirth) : null}
                    onChange={dateHandlerGenerator(`applicationData.applicants[${index}].dateOfBirth`, clearValidation)}
                    onAccept={applicantFieldValidations(index).dateOfBirth}
                    disableFuture
                    slotProps={{
                      textField: {
                        error: !!errors[`applicationData.applicants[${index}].dateOfBirth`],
                        helperText: errors[`applicationData.applicants[${index}].dateOfBirth`] ?? ' ',
                        onBlur: applicantFieldValidations(index).dateOfBirth,
                        inputProps: {
                          'data-nid-target': `owner-info-date-of-birth-${index}`,
                        },
                      },
                    }}
                    openTo="year"
                    views={['year', 'month', 'day']}
                    disabled={isFieldDisabled(`applicationData.applicants.${index}.dateOfBirth`)}
                  />
                </div>
              </div>

              <div className={styles.twoColumnRow}>
                <TextField
                  name="ssnOrItin"
                  onFocus={handleInputFocus}
                  label={t('digital_onboarding.banking.owner_info.label_ssn')}
                  value={getValueSSN(applicant, index, errors[`applicationData.applicants[${index}].ssnOrItin`])}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => onChangeSSN(e, index)}
                  onBlur={() => onBlurSSN(index)}
                  error={!!errors[`applicationData.applicants[${index}].ssnOrItin`]}
                  helperText={errors[`applicationData.applicants[${index}].ssnOrItin`] ?? ' '}
                  slotProps={{
                    htmlInput: {
                      inputMode: 'numeric',
                      'data-nid-target': `owner-info-ssn-${index}`,
                    } as React.InputHTMLAttributes<HTMLInputElement>,
                  }}
                />

                {allowAdditionalOwners && (
                  <TextField
                    label={t('digital_onboarding.owner_officer_section.label_ownership_percentage')}
                    name="ownershipPercentage"
                    value={applicant?.ownershipPercentage?.toString() || ''}
                    onChange={numberHandlerGenerator(
                      `applicationData.applicants[${index}].ownershipPercentage`,
                      clearValidation,
                    )}
                    onBlur={applicantFieldValidations(index).ownershipPercentage}
                    error={!!errors[`applicationData.applicants[${index}].ownershipPercentage`]}
                    helperText={errors[`applicationData.applicants[${index}].ownershipPercentage`] ?? ' '}
                    onFocus={handleInputFocus}
                    slotProps={{
                      htmlInput: {
                        maxLength: 3,
                        inputMode: 'numeric',
                        'data-nid-target': `owner-info-ownership-percentage-${index}`,
                      } as React.InputHTMLAttributes<HTMLInputElement>,
                    }}
                    disabled={isFieldDisabled(`applicationData.applicants.${index}.ownershipPercentage`)}
                  />
                )}
              </div>

              <p className={styles.text}>{t('digital_onboarding.banking.owner_info.po_boxes')}</p>

              <div className={styles.twoColumnRow}>
                <TextField
                  name="homeStreetAddress"
                  onFocus={handleInputFocus}
                  label={t('digital_onboarding.banking.business_info.label_street')}
                  value={applicant?.homeAddress?.street ?? ''}
                  onChange={textHandlerGenerator(
                    `applicationData.applicants[${index}].homeAddress.street`,
                    clearValidation,
                  )}
                  onBlur={applicantFieldValidations(index).homeStreetAddress}
                  error={!!errors[`applicationData.applicants[${index}].homeAddress.street`]}
                  helperText={errors[`applicationData.applicants[${index}].homeAddress.street`] ?? ' '}
                  slotProps={{
                    htmlInput: {
                      maxLength: 500,
                      autoCapitalize: 'sentences',
                      'data-nid-target': `owner-info-street-${index}`,
                    } as React.InputHTMLAttributes<HTMLInputElement>,
                  }}
                  disabled={isFieldDisabled(`applicationData.applicants.${index}.homeAddress.street`)}
                />

                <TextField
                  name="secondaryStreetAddress"
                  onFocus={handleInputFocus}
                  label={t('digital_onboarding.banking.business_info.label_secondary_street')}
                  value={applicant?.homeAddress?.secondaryStreet ?? ''}
                  onChange={textHandlerGenerator(
                    `applicationData.applicants[${index}].homeAddress.secondaryStreet`,
                    clearValidation,
                  )}
                  onBlur={applicantFieldValidations(index).secondaryStreetAddress}
                  error={!!errors[`applicationData.applicants[${index}].homeAddress.secondaryStreet`]}
                  helperText={errors[`applicationData.applicants[${index}].homeAddress.secondaryStreet`] ?? ' '}
                  slotProps={{
                    htmlInput: {
                      maxLength: 500,
                      autoCapitalize: 'sentences',
                      'data-nid-target': `owner-info-secondary-street-${index}`,
                    } as React.InputHTMLAttributes<HTMLInputElement>,
                  }}
                  disabled={isFieldDisabled(`applicationData.applicants.${index}.homeAddress.secondaryStreet`)}
                />
              </div>

              <div className={styles.twoColumnRow}>
                <TextField
                  name="tertiaryStreetAddress"
                  onFocus={handleInputFocus}
                  label={t('digital_onboarding.banking.business_info.label_tertiary_street')}
                  value={applicant?.homeAddress?.tertiaryStreet ?? ''}
                  onChange={textHandlerGenerator(
                    `applicationData.applicants[${index}].homeAddress.tertiaryStreet`,
                    clearValidation,
                  )}
                  onBlur={applicantFieldValidations(index).tertiaryStreetAddress}
                  error={!!errors[`applicationData.applicants[${index}].homeAddress.tertiaryStreet`]}
                  helperText={errors[`applicationData.applicants[${index}].homeAddress.tertiaryStreet`] ?? ' '}
                  slotProps={{
                    htmlInput: {
                      maxLength: 500,
                      autoCapitalize: 'sentences',
                      'data-nid-target': `owner-info-tertiary-street-${index}`,
                    } as React.InputHTMLAttributes<HTMLInputElement>,
                  }}
                  disabled={isFieldDisabled(`applicationData.applicants.${index}.homeAddress.tertiaryStreet`)}
                />

                <TextField
                  name="city"
                  onFocus={handleInputFocus}
                  label={t('digital_onboarding.common_section.label_city')}
                  value={applicant?.homeAddress?.city ?? ''}
                  onChange={textHandlerGenerator(
                    `applicationData.applicants[${index}].homeAddress.city`,
                    clearValidation,
                  )}
                  onBlur={applicantFieldValidations(index).city}
                  error={!!errors[`applicationData.applicants[${index}].homeAddress.city`]}
                  helperText={errors[`applicationData.applicants[${index}].homeAddress.city`] ?? ' '}
                  slotProps={{
                    htmlInput: {
                      maxLength: 500,
                      'data-nid-target': `owner-info-city-${index}`,
                    } as React.InputHTMLAttributes<HTMLInputElement>,
                  }}
                  disabled={isFieldDisabled(`applicationData.applicants.${index}.homeAddress.city`)}
                />
              </div>

              <div className={styles.twoColumnRowWithSplit}>
                <FormControl error={!!errors[`applicationData.applicants[${index}].homeAddress.state`]}>
                  <InputLabel id={`owner-state-label-${index}`}>
                    {t('digital_onboarding.common_section.label_state')}
                  </InputLabel>
                  <Select
                    onFocus={handleInputFocus}
                    name="ownerState"
                    labelId={`owner-state-label-${index}`}
                    label={t('digital_onboarding.common_section.label_state')}
                    value={applicant?.homeAddress?.state ?? ''}
                    onChange={selectDropdownHandlerGenerator(
                      `applicationData.applicants[${index}].homeAddress.state`,
                      clearValidation,
                    )}
                    fullWidth
                    slotProps={{
                      input: {
                        'data-nid-target': `owner-info-state-${index}`,
                      } as React.InputHTMLAttributes<HTMLInputElement>,
                    }}
                    disabled={isFieldDisabled(`applicationData.applicants.${index}.homeAddress.state`)}
                  >
                    {STATE_OPTIONS_WITH_ARMED_FORCES.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>
                    {errors[`applicationData.applicants[${index}].homeAddress.state`] ?? ' '}
                  </FormHelperText>
                </FormControl>

                <div className={styles.splitColumn}>
                  <TextField
                    name="zipCode"
                    onFocus={handleInputFocus}
                    label={t('digital_onboarding.common_section.label_zip')}
                    value={applicant?.homeAddress?.zip ?? ''}
                    onChange={numberHandlerGenerator(
                      `applicationData.applicants[${index}].homeAddress.zip`,
                      clearValidation,
                    )}
                    onBlur={applicantFieldValidations(index).zipCode}
                    error={!!errors[`applicationData.applicants[${index}].homeAddress.zip`]}
                    helperText={errors[`applicationData.applicants[${index}].homeAddress.zip`] ?? ' '}
                    slotProps={{
                      htmlInput: {
                        maxLength: 5,
                        inputMode: 'numeric',
                        'data-nid-target': `owner-info-zip-${index}`,
                      } as React.InputHTMLAttributes<HTMLInputElement>,
                    }}
                    disabled={isFieldDisabled(`applicationData.applicants.${index}.homeAddress.zip`)}
                  />

                  <div className={styles.countryView}>
                    <TextField
                      label={t('digital_onboarding.common_section.label_country')}
                      value={'USA'}
                      disabled
                      slotProps={{
                        htmlInput: {
                          'data-nid-target': `owner-info-country-${index}`,
                        } as React.InputHTMLAttributes<HTMLInputElement>,
                      }}
                    />
                    <p className={styles.fieldText}>{t('digital_onboarding.banking.owner_info.country_helper_text')}</p>
                  </div>
                </div>
              </div>

              <div className={styles.twoColumnRow}>
                <TextField
                  name="email"
                  onFocus={handleInputFocus}
                  label={t('digital_onboarding.banking.owner_info.label_email')}
                  value={applicant?.email ?? ''}
                  onChange={textHandlerGenerator(`applicationData.applicants[${index}].email`, clearValidation, true)}
                  onBlur={applicantFieldValidations(index).email}
                  error={!!errors[`applicationData.applicants[${index}].email`]}
                  helperText={errors[`applicationData.applicants[${index}].email`] ?? ' '}
                  slotProps={{
                    htmlInput: {
                      maxLength: 254,
                      inputMode: 'email',
                      'data-nid-target': `owner-info-email-${index}`,
                    } as React.InputHTMLAttributes<HTMLInputElement>,
                  }}
                  disabled={index === 0 || isFieldDisabled(`applicationData.applicants.${index}.email`)}
                />
                <TextField
                  name="mobilePhoneNumber"
                  onFocus={handleInputFocus}
                  label={t('digital_onboarding.banking.owner_info.label_personal_phone')}
                  value={phoneFormatter(applicant?.mobilePhoneNumber) ?? ''}
                  onChange={numberHandlerGenerator(
                    `applicationData.applicants[${index}].mobilePhoneNumber`,
                    clearValidation,
                  )}
                  onBlur={applicantFieldValidations(index).mobilePhoneNumber}
                  error={!!errors[`applicationData.applicants[${index}].mobilePhoneNumber`]}
                  helperText={errors[`applicationData.applicants[${index}].mobilePhoneNumber`] ?? ' '}
                  slotProps={{
                    htmlInput: {
                      maxLength: 16,
                      inputMode: 'numeric',
                      'data-nid-target': `owner-info-phone-${index}`,
                    } as React.InputHTMLAttributes<HTMLInputElement>,
                  }}
                  disabled={isFieldDisabled(`applicationData.applicants.${index}.mobilePhoneNumber`)}
                />
              </div>
            </div>
          );
        })}

      {allowAdditionalOwners && (
        <>
          <div className={styles.additionalOwnerButtonView}>
            <h3 className={styles.additionalOwnerHeader}>
              {t('digital_onboarding.banking.owner_info.additional_owner_header')}
            </h3>

            {applicationData?.applicants && applicationData?.applicants?.length === 4 ? (
              <p className={styles.maxOwnersText}>{t('digital_onboarding.banking.owner_info.max_owners')}</p>
            ) : (
              <p className={styles.additionalOwnerDescription}>
                {t('digital_onboarding.banking.owner_info.additional_owner_description_web')}
                <span className={styles.boldText}>
                  {t('digital_onboarding.banking.owner_info.additional_owner_description_bold')}
                </span>
              </p>
            )}

            <IconButton
              className={styles.addOwnerButton}
              onClick={applicationData?.applicants?.length === 4 ? undefined : throttledAddOwner}
            >
              <AddCircleIcon
                color={
                  applicationData?.applicants && applicationData?.applicants?.length === 4 ? 'disabled' : 'success'
                }
              />
            </IconButton>
          </div>
        </>
      )}
      <div className={styles.footer}>
        <ContinueButton
          title={t('digital_onboarding.next')}
          buttonStyles={baseStyles.buttonStyle}
          handleContinueClick={handleContinueClick}
          isPatching={isPatching}
        />
      </div>
      <RemoveOwnerConfirmationModal
        open={showRemoveOwnerModal}
        onCancel={handleCancelRemove}
        onConfirm={handleConfirmRemove}
      />
    </div>
  );
};
